import { useEffect, useRef } from 'react';

import { toast } from 'react-toastify';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { FaLongArrowAltRight, FaTrash } from 'react-icons/fa';
import { IoWarning } from 'react-icons/io5';
import styled from 'styled-components';

import { colors } from 'constants/colors';

import { TotemLabel } from 'components/TotemLabel';
import { Loader, LoaderModeType } from 'components/Loader';
import { DetailLink, DetailFormValue } from 'components/DetailsView';

import { FrenchDate } from 'helpers/dateTimes';
import { ProductSelect } from 'components/Select/ProductSelect';
import { useGetTransferableProductsQuery } from 'data/__generated__';
import { round } from 'lodash';
import { StockTransferFormData } from '../stockTransfer.schema';

export const ProductsToTransferTable = ({
    originColumnId,
    destinationColumnId,
    isTransferValidated,
    transferDate,
}: {
    originColumnId: string;
    destinationColumnId: string;
    isTransferValidated?: boolean;
    transferDate?: FrenchDate;
}) => {
    const { watch, setValue, register } = useFormContext<StockTransferFormData>();
    const { fields, append, remove } = useFieldArray({
        name: 'products',
    });
    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
    } = useGetTransferableProductsQuery({
        skip: isTransferValidated || !originColumnId || !destinationColumnId,
        variables: {
            originColumnId,
            destinationColumnId,
            transferDate,
        },
    });

    const products = watch('products');
    const previousProductsDataRef = useRef<typeof productsData>();

    useEffect(() => {
        // Only update when productsData changes, not when products change
        if (productsData && productsData !== previousProductsDataRef.current && products.length > 0) {
            products.forEach((product, index) => {
                const transferableProduct = productsData.transferableProducts.find(
                    ({ productId }) => product.productId === productId,
                );

                if (transferableProduct) {
                    const newOriginStock = transferableProduct.originStockAvailable ?? 0;
                    const newDestinationStock = transferableProduct.destinationStockAvailable ?? 0;

                    // Only update if values have changed to avoid unnecessary re-renders
                    if (product.originStockAvailable !== newOriginStock) {
                        setValue(`products.${index}.originStockAvailable`, newOriginStock, { shouldDirty: false });
                    }
                    if (product.destinationStockAvailable !== newDestinationStock) {
                        setValue(`products.${index}.destinationStockAvailable`, newDestinationStock, {
                            shouldDirty: false,
                        });
                    }
                }
            });
            previousProductsDataRef.current = productsData;
        }
    }, [productsData, setValue, products.length]);

    useEffect(() => {
        if (
            !isTransferValidated &&
            !productsLoading &&
            (productsError || (!productsData && originColumnId && destinationColumnId))
        ) {
            toast.error('Une erreur est survenue lors de la récupération des produits transférables');
        }
    }, [isTransferValidated, productsLoading, productsError, productsData, originColumnId, destinationColumnId]);

    if (productsLoading) {
        return (
            <LoaderContainer>
                <Loader mode={LoaderModeType.Spin} />
            </LoaderContainer>
        );
    }

    if (!isTransferValidated && (productsError || (!productsData && originColumnId && destinationColumnId))) {
        return null;
    }

    const onSelect = (productId?: string | null) => {
        if (productId) {
            const product = productsData?.transferableProducts.find((product) => product.productId === productId);
            if (product) {
                const existingProduct = products.find((p) => p.productId === productId);
                if (existingProduct) {
                    toast.warn('Ce produit est déjà dans la liste');
                    return;
                }
                append({
                    productId: product.productId,
                    quantity: 0,
                    packages: 0,
                    palette: 0,
                    fullname: product.fullname,
                    conditionning: product.conditionning,
                    packagesPerBatch: product.packagesPerBatch,
                    originStockAvailable: product.originStockAvailable ?? 0,
                    destinationStockAvailable: product.destinationStockAvailable ?? 0,
                });
            } else {
                toast.error("Le produit selectionné n'est pas un transferable dans ce site");
            }
        }
    };

    const handleProductDelete = (index: number) => {
        remove(index);
    };

    const handleQuantityChange = (
        index: number,
        newQuantity: number,
        conditionning: number,
        packagesPerBatch: number,
    ) => {
        setValue(`products.${index}.quantity`, newQuantity, { shouldDirty: true });
        if (conditionning > 0) {
            setValue(`products.${index}.packages`, round(newQuantity / conditionning, 2), { shouldDirty: false });
        }
        if (conditionning > 0 && packagesPerBatch > 0) {
            setValue(`products.${index}.palette`, round(newQuantity / conditionning / packagesPerBatch, 2), {
                shouldDirty: false,
            });
        }
    };

    const handlePackagesChange = (
        index: number,
        newPackages: number,
        conditionning: number,
        packagesPerBatch: number,
    ) => {
        const newQuantity = round(newPackages * conditionning);
        setValue(`products.${index}.packages`, newPackages, { shouldDirty: true });
        setValue(`products.${index}.quantity`, newQuantity, { shouldDirty: false });
        if (packagesPerBatch > 0) {
            setValue(`products.${index}.palette`, round(newPackages / packagesPerBatch, 2), { shouldDirty: false });
        }
    };

    const handlePaletteChange = (
        index: number,
        newPalette: number,
        conditionning: number,
        packagesPerBatch: number,
    ) => {
        const newPackages = round(newPalette * packagesPerBatch);
        const newQuantity = round(newPackages * conditionning);
        setValue(`products.${index}.palette`, newPalette, { shouldDirty: true });
        setValue(`products.${index}.packages`, newPackages, { shouldDirty: false });
        setValue(`products.${index}.quantity`, newQuantity, { shouldDirty: false });
    };

    return (
        <Container>
            <div>
                <ProductSelect dataTest="select-product" value={null} onChange={(product) => onSelect(product?._id)} />
            </div>
            {fields.length > 0 ? (
                <>
                    <TotemLabel>Produits à transférer</TotemLabel>
                    <ProductsTable>
                        <thead>
                            <tr>
                                <ProductsTableHeaderCell>Produit</ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>Colisage</ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>Palettisation</ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>
                                    {!isTransferValidated ? 'Palettes à transférer' : 'Palettes transférées'}
                                </ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>
                                    {!isTransferValidated ? 'Colis à transférer' : 'Colis transférés'}
                                </ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>
                                    {!isTransferValidated ? 'Quantité à transférer' : 'Quantité transférée'}
                                </ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>
                                    Stock dispo à l'origine
                                </ProductsTableHeaderCell>
                                <ProductsTableHeaderCell $isDataCentered>
                                    Stock dispo à destination
                                </ProductsTableHeaderCell>
                                {!isTransferValidated ? (
                                    <ProductsTableHeaderCell $isDataCentered>Supprimer</ProductsTableHeaderCell>
                                ) : null}
                            </tr>
                        </thead>
                        <ProductsTableBody>
                            {fields.map((field, index) => {
                                const product = products[index];
                                if (!product) return null;

                                const {
                                    productId,
                                    fullname,
                                    conditionning = 0,
                                    packagesPerBatch = 0,
                                    quantity = 0,
                                    originStockAvailable = 0,
                                    destinationStockAvailable = 0,
                                } = product;
                                const transferableProduct = productsData?.transferableProducts.find(
                                    (p) => p.productId === productId,
                                );

                                return (
                                    <tr key={field.id}>
                                        <ProductsTableBodyCell>
                                            <ProductNameContainer>
                                                <DetailLink
                                                    name={fullname || 'Sans nom'}
                                                    value={productId}
                                                    path="product"
                                                />
                                                {!isTransferValidated && !transferableProduct ? (
                                                    <ErrorLabel data-test="product-not-transferable-error">
                                                        Le produit selectionné n'est pas un produit microstore
                                                    </ErrorLabel>
                                                ) : null}
                                            </ProductNameContainer>
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>{conditionning}</ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            {packagesPerBatch}
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            {packagesPerBatch > 1 ? (
                                                <QuantityInputOuterContainer>
                                                    <QuantityInputInnerContainer>
                                                        <DetailFormValue
                                                            type="number"
                                                            inputMode="decimal"
                                                            step="1"
                                                            min="0"
                                                            disabled={isTransferValidated}
                                                            {...register(`products.${index}.palette` as const, {
                                                                valueAsNumber: true,
                                                                onChange: (e) => {
                                                                    const value = parseFloat(e.target.value) || 0;
                                                                    handlePaletteChange(
                                                                        index,
                                                                        value,
                                                                        conditionning,
                                                                        packagesPerBatch,
                                                                    );
                                                                },
                                                            })}
                                                            style={{
                                                                textAlign: 'center',
                                                                width: '100px',
                                                            }}
                                                            data-test="product-palette"
                                                        />
                                                    </QuantityInputInnerContainer>
                                                </QuantityInputOuterContainer>
                                            ) : null}
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            {conditionning > 1 ? (
                                                <QuantityInputOuterContainer>
                                                    <QuantityInputInnerContainer>
                                                        <DetailFormValue
                                                            type="number"
                                                            inputMode="decimal"
                                                            step="1"
                                                            min="0"
                                                            disabled={isTransferValidated}
                                                            {...register(`products.${index}.packages` as const, {
                                                                valueAsNumber: true,
                                                                onChange: (e) => {
                                                                    const value = parseFloat(e.target.value) || 0;
                                                                    handlePackagesChange(
                                                                        index,
                                                                        value,
                                                                        conditionning,
                                                                        packagesPerBatch,
                                                                    );
                                                                },
                                                            })}
                                                            style={{
                                                                textAlign: 'center',
                                                                width: '100px',
                                                            }}
                                                            data-test="product-colis"
                                                        />
                                                    </QuantityInputInnerContainer>
                                                </QuantityInputOuterContainer>
                                            ) : null}
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            <QuantityInputOuterContainer>
                                                <QuantityInputInnerContainer>
                                                    <DetailFormValue
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        disabled={isTransferValidated}
                                                        {...register(`products.${index}.quantity` as const, {
                                                            valueAsNumber: true,
                                                            onChange: (e) => {
                                                                const value = parseInt(e.target.value, 10) || 0;
                                                                handleQuantityChange(
                                                                    index,
                                                                    value,
                                                                    conditionning,
                                                                    packagesPerBatch,
                                                                );
                                                            },
                                                        })}
                                                        style={{
                                                            textAlign: 'center',
                                                            width: '100px',
                                                        }}
                                                        data-test="product-quantity"
                                                    />
                                                    {quantity < 0 && (
                                                        <div
                                                            data-test="quantity-error"
                                                            style={{ color: 'red', fontSize: '12px' }}
                                                        >
                                                            La quantité ne peut pas être négative
                                                        </div>
                                                    )}
                                                </QuantityInputInnerContainer>
                                            </QuantityInputOuterContainer>
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            <StockDelta>
                                                {!isTransferValidated ? (
                                                    transferableProduct ? (
                                                        <>
                                                            <span>{transferableProduct.originStockAvailable}</span>
                                                            <FaLongArrowAltRight size={25} color={colors.red} />
                                                            <span>
                                                                {transferableProduct.originStockAvailable - quantity}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <ErrorLabel>Non transférable, pas d'info</ErrorLabel>
                                                    )
                                                ) : (
                                                    <>
                                                        <span>{originStockAvailable + quantity}</span>
                                                        <FaLongArrowAltRight size={25} color={colors.red} />
                                                        <span>{originStockAvailable}</span>
                                                    </>
                                                )}
                                                {originStockAvailable - quantity < 0 ? (
                                                    <WarningIcon data-test="warning-icon" size={25} />
                                                ) : null}
                                            </StockDelta>
                                        </ProductsTableBodyCell>
                                        <ProductsTableBodyCell $isDataCentered>
                                            <StockDelta>
                                                {!isTransferValidated ? (
                                                    transferableProduct ? (
                                                        <>
                                                            <span>{transferableProduct.destinationStockAvailable}</span>
                                                            <FaLongArrowAltRight size={25} color={colors.green} />
                                                            <span>
                                                                {transferableProduct.destinationStockAvailable +
                                                                    quantity}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <ErrorLabel>Non transférable, pas d'info</ErrorLabel>
                                                    )
                                                ) : (
                                                    <>
                                                        <span>{destinationStockAvailable - quantity}</span>
                                                        <FaLongArrowAltRight size={25} color={colors.green} />
                                                        <span>{destinationStockAvailable}</span>
                                                    </>
                                                )}
                                            </StockDelta>
                                        </ProductsTableBodyCell>
                                        {!isTransferValidated ? (
                                            <ProductsTableBodyCell $isDataCentered>
                                                <DeleteIconContainer onClick={() => handleProductDelete(index)}>
                                                    <FaTrash data-test="trash-icon" size={20} color={colors.red} />
                                                </DeleteIconContainer>
                                            </ProductsTableBodyCell>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </ProductsTableBody>
                    </ProductsTable>
                </>
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    color: ${({ theme }) => theme.textColor};

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const ErrorLabel = styled.div`
    font-weight: 800;
    color: ${colors.red};
    text-align: center;
`;

const ProductsTable = styled.table`
    border-radius: ${({ theme }) => theme.borderRadius};
    border-spacing: 0px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ProductsTableBody = styled.thead`
    & > :not(:first-child) td {
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

const ProductsTableHeaderCell = styled.th<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ProductsTableBodyCell = styled.td<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const ProductNameContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const QuantityInputOuterContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const QuantityInputInnerContainer = styled.div`
    width: 100px;
`;

const WarningIcon = styled(IoWarning)`
    color: ${({ theme }) => theme.warningColor};
`;

const StockDelta = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const DeleteIconContainer = styled.div`
    display: inline;
    padding: 5px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const LoaderContainer = styled.div`
    padding: 15px;
`;
